import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  useTheme,
  CircularProgress,
  Typography,
} from "@mui/material";
import CustomStepper from "../../components/common/Stepper";
import AddStreamDetails from "./steps/AddStreamDetails";
import AddStreamGraphicsMedia from "./steps/AddStreamGraphicsMedia";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { tokens } from "../../theme";
import TeamAssignment from "./steps/AddStreamTeamAssignment";
import apiService from "../../service/Api";
import { toast } from "react-toastify";
import { checkPermission } from "../../service/permissionGuard";
import { PERMISSIONS } from "../../constants/permissions";
import AddStreamConfirmation from "./steps/AddStreamConfirmation";
import { IStreamFormData } from "../../types/streams";
import { useNavigate } from "react-router-dom";

interface BookingData {
  booking_type?: string;
  service?: string;
  events?: Array<{
    id?: number;
    venue?: { id?: number };
    title?: string;
    start_time?: string;
    end_time?: string;
    start_date_of_event?: string;
    special_requiest?: string;
    special_notes?: string;
    notes?: string;
  }>;
  services?: Array<{
    id?: number;
    venue?: { id?: number };
    type_of_service?: string;
    start_time?: string;
    end_time?: string;
    date_of_service?: string;
    special_request?: string;
    service_notes?: string;
    preacher_name?: string;
    mc_name?: string;
    eulogist_name?: string;
  }>;
}

const appendFormValue = (
  formData: FormData,
  key: string,
  value: unknown
) => {
  if (value === undefined || value === null) return;
  formData.append(key, String(value));
};

const steps = [
  "Stream Details",
  "Team Assignment",
  "Graphics & Media",
  "Confirmation",
];

const AddStream: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<IStreamFormData>({
    booking: null,
    title: "",
    serviceType: "",
    startTime: "",
    endTime: "",
    date: "",
    venue: null,
    specialNotes: "",
    travelDuration: null,
    youtube_stream_link: "",
    website_stream_link: "",
    go_fund_me_link: "",
    callTime: "",
    travellingFrom: "",
    travellingTo: "",
    setupFrom: "",
    setupTo: "",
    estimatedWrap: "",
    priest_name: "",
    mc_name: "",
    eulogist_name: "",
    other: "",
    crew: { members: [] },
    call_sheets: [],
    thumbnails: [],
    slideshows: [],
    programs: [],
  });
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();
  const handleInputChange = useCallback(
    (field: keyof IStreamFormData, value: string) => {
      let val: number | string | null | undefined = value;
      if (field === "booking" || field === "venue" || field === "service_id") {
        val = value ? parseInt(value, 10) : null;
      } else if (field === "travelDuration") {
        val = value ? parseInt(value, 10) : null;
      } else if (field === "title") {
        val = value || undefined;
      }
      setFormData((prev) => ({
        ...prev,
        [field]: val,
      }));
    },
    []
  );

  const handleStepClick = (index: number) => {
    setActiveStep(index);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleThumbnailChange = (files: File[]) => {
    setFormData((prev) => ({ ...prev, thumbnails: files }));
  };

  const handleSlideshowChange = (files: File[]) => {
    setFormData((prev) => ({ ...prev, slideshows: files }));
  };

  const handleProgramChange = (files: File[]) => {
    setFormData((prev) => ({ ...prev, programs: files }));
  };

  const handleSubmit = async () => {
    if (
      !formData.booking ||
      !formData.venue ||
      !formData.startTime ||
      !formData.endTime ||
      !formData.date
    ) {
      toast.error(
        "Please fill in all required fields (Booking, Venue, Service Type, Start Time, End Time, Date)."
      );
      return;
    }
    setLoading(true);
    try {
      checkPermission(PERMISSIONS.STREAM_ADD);
      const bookingResponse = await apiService.get<unknown>(
        `/api/bookings/booking/${formData.booking}/`
      );
      const bookingData = bookingResponse.data as BookingData;
      const bookingType = bookingData.booking_type;
      const isConferenceOrEvent =
        bookingType === "CONFERENCE" || bookingType === "EVENT";

      const formDataToSend = new FormData();

      if (isConferenceOrEvent) {
        const eventData: Record<string, unknown> = {
          booking: formData.booking ? formData.booking.toString() : undefined,
          venue: formData.venue ? formData.venue.toString() : undefined,
          title: formData.title || formData.serviceType || undefined,
          start_date_of_event: formData.date || undefined,
          end_date_of_event: formData.date || undefined,
          start_time: formData.startTime || undefined,
          end_time: formData.endTime || undefined,
          special_notes: formData.specialNotes || undefined,
          notes: formData.other || undefined,
        };
        Object.entries(eventData).forEach(([key, value]) => {
          appendFormValue(formDataToSend, `event[${key}]`, value);
        });
      } else {
        const serviceData: Record<string, unknown> = {
          booking: formData.booking ? formData.booking.toString() : undefined,
          venue: formData.venue ? formData.venue.toString() : undefined,
          type_of_service: formData.serviceType || undefined,
          start_time: formData.startTime || undefined,
          end_time: formData.endTime || undefined,
          date_of_service: formData.date || undefined,
          special_request: formData.specialNotes || undefined,
          service_notes: formData.other || undefined,
          preacher_name: formData.priest_name || undefined,
          mc_name: formData.mc_name || undefined,
          eulogist_name: formData.eulogist_name || undefined,
        };
        Object.entries(serviceData).forEach(([key, value]) => {
          appendFormValue(formDataToSend, `service[${key}]`, value);
        });
      }

      const streamData: Record<string, unknown> = {
        title: formData.title || undefined,
        start: formData.startTime || undefined,
        end: formData.endTime || undefined,
        date: formData.date || undefined,
        youtube_stream_link: formData.youtube_stream_link || undefined,
        website_stream_link: formData.website_stream_link || undefined,
        go_fund_me_link: formData.go_fund_me_link || undefined,
      };
      Object.entries(streamData).forEach(([key, value]) => {
        appendFormValue(formDataToSend, `stream[${key}]`, value);
      });

      const topLevelFields: {
        callTime?: string;
        travellingFrom?: string;
        travellingTo?: string;
        setupFrom?: string;
        setupTo?: string;
        estimatedWrap?: string;
        travelDuration?: number | null;
        crew?: IStreamFormData["crew"];
        call_sheets?: IStreamFormData["call_sheets"];
        thumbnails?: File[];
        slideshows?: File[];
        programs?: File[];
      } = {
        callTime: formData.callTime || undefined,
        travellingFrom: formData.travellingFrom || undefined,
        travellingTo: formData.travellingTo || undefined,
        setupFrom: formData.setupFrom || undefined,
        setupTo: formData.setupTo || undefined,
        estimatedWrap: formData.estimatedWrap || undefined,
        travelDuration: formData.travelDuration ?? undefined,
        crew: formData.crew,
        call_sheets: formData.call_sheets || [],
        thumbnails: formData.thumbnails || [],
        slideshows: formData.slideshows || [],
        programs: formData.programs || [],
      };

      Object.entries(topLevelFields).forEach(([key, value]) => {
        if (value === undefined) return;
        if (key === "crew") {
          formDataToSend.append(key, JSON.stringify(value));
        } else if (key === "call_sheets") {
          (value as IStreamFormData["call_sheets"]).forEach((sheet, index) => {
            formDataToSend.append(
              `call_sheets[${index}]`,
              JSON.stringify(sheet)
            );
          });
        } else if (["thumbnails", "slideshows", "programs"].includes(key)) {
          (value as File[]).forEach((file) => {
            formDataToSend.append(`${key}[]`, file);
          });
        } else {
          appendFormValue(formDataToSend, key, value);
        }
      });

      // Log FormData for debugging
      const formDataEntries: Record<string, unknown> = {};
      formDataToSend.forEach((value, key) => {
        formDataEntries[key] = value;
      });

      await apiService.post(
        "/api/streams/stream/full-create/",
        formDataToSend,
        false
      );
      toast.success("Stream created successfully");
      navigate("/stream-management/", { replace: true });
    } catch (err: unknown) {
      console.error("Failed to create stream:", err);
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to create stream. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <AddStreamDetails
            formData={formData}
            handleInputChange={handleInputChange}
            isEdit={false}
          />
        );
      case 1:
        return <TeamAssignment formData={formData} setFormData={setFormData} />;
      case 2:
        return (
          <AddStreamGraphicsMedia
            handleThumbnailChange={handleThumbnailChange}
            handleSlideshowChange={handleSlideshowChange}
            handleProgramChange={handleProgramChange}
          />
        );
      case 3:
        return <AddStreamConfirmation formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, boxShadow: 0 }}>
        <Typography variant="h4" gutterBottom>
          Add Stream
        </Typography>
        <Box sx={{ mb: 4 }}>
          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            onStepClick={handleStepClick}
          />
        </Box>

        {renderStepContent(activeStep)}

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: "row",
            zIndex: 1,
            justifyContent: "space-between",
            mt: 3,
          }}
        >
          <Button
            startIcon={<KeyboardArrowLeft />}
            sx={{
              backgroundColor: colors.primary[800],
              pr: 5,
              pl: 5,
              mr: 1,
              pt: 1,
              pb: 1,
              color: colors.primary[100],
            }}
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Previous
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          {activeStep < steps.length - 1 && (
            <Button
              sx={{
                backgroundColor: colors.primary[400],
                pr: 5,
                pl: 5,
                mr: 1,
                pt: 1,
                pb: 1,
                color: "white",
              }}
              disabled={loading}
              onClick={handleNext}
              endIcon={<KeyboardArrowRight />}
            >
              Next
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <Button
              sx={{
                backgroundColor: colors.primary[400],
                pr: 5,
                pl: 5,
                mr: 1,
                pt: 1,
                pb: 1,
                color: "white",
              }}
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Create Stream"
              )}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AddStream;
